import { Translate } from '@components'

import { FormattedDateTime, InternalAccessor, TableColumn } from '@billy/design-system'
import { useMemo } from 'react'

import { getCurrentLocale } from '../../../../../../../utils'
import { useQuotesList } from '../../../context/quotesList'
import { QuotesTableAccessor } from '../../../enums/quotesTableAccessor'
import { QuotesTableRow } from '../../../types/quotesTableRow'
import { QuotesTableStaticActions } from '../elements/QuotesTableStaticActions'
import { QuoteStatus } from '../elements/QuoteStatus'

interface UseQuotesTableColumnsReturn {
  columns: TableColumn<QuotesTableRow>[]
}

export const useQuotesTableColumns = (): UseQuotesTableColumnsReturn => {
  const { columns: activeColumns } = useQuotesList()

  const columns: TableColumn<QuotesTableRow>[] = useMemo(() => {
    const locale = getCurrentLocale()

    const mappedColumns: TableColumn<QuotesTableRow>[] = activeColumns.map((column) => {
      switch (column) {
        case QuotesTableAccessor.Status:
          return {
            accessor: QuotesTableAccessor.Status,
            alignment: 'left',
            Cell: ({ row, value: status }) => (
              <QuoteStatus
                deliveryStatus={row.original.deliveryStatus}
                invoiceState={row.original.invoiceState}
                status={status}
              />
            ),
            size: 'xxxs',
            sortable: false,
          }
        case QuotesTableAccessor.QuoteNumber:
          return {
            accessor: QuotesTableAccessor.QuoteNumber,
            Cell: ({ value: estimateNumber }) => estimateNumber ?? '—',
            Header: <Translate value="quotes.table.number" />,
            hidden: true,
            size: 'xxs',
            truncate: true,
          }
        case QuotesTableAccessor.IssueDate:
          return {
            accessor: QuotesTableAccessor.IssueDate,
            Cell: ({ value: date }) => date && <FormattedDateTime locale={locale} value={date} />,
            fixedSize: true,
            Header: <Translate value="quotes.table.issue_date" />,
            size: 's',
            sortable: true,
            truncate: true,
          }
        case QuotesTableAccessor.DueDate:
          return {
            accessor: QuotesTableAccessor.DueDate,
            Cell: ({ value: date }) => date && <FormattedDateTime locale={locale} value={date} />,
            fixedSize: true,
            Header: <Translate value="quotes.table.due_date" />,
            size: 's',
            sortable: true,
            truncate: true,
          }
        case QuotesTableAccessor.ContactId:
          return {
            accessor: QuotesTableAccessor.ContactId,
            Header: <Translate value="quotes.table.customer" />,
            id: 'contact.name',
            size: 's',
            sortable: true,
            truncate: true,
          }
        case QuotesTableAccessor.Description:
          return {
            accessor: QuotesTableAccessor.Description,
            Header: <Translate value="quotes.table.description" />,
            size: 'm',
            truncate: true,
          }
        case QuotesTableAccessor.NetTotal:
          return {
            accessor: QuotesTableAccessor.NetTotal,
            alignment: 'right',
            Header: <Translate value="quotes.table.excl_vat_short" />,
            size: 's',
            sortable: true,
            truncate: true,
          }
        default:
          throw new Error(`Unsupported column: ${column}`)
      }
    })

    const staticActionsColumn: TableColumn<QuotesTableRow> = {
      accessor: InternalAccessor.StaticActions,
      Cell: ({ row }) => {
        const {
          convertToFinalInvoiceUrl,
          convertToInvoiceUrl,
          documentLocale,
          documentType,
          duplicateUrl,
          editUrl,
          externalContactId,
          id,
          quoteStatus,
          [QuotesTableAccessor.QuoteNumber]: estimateNumber,
        } = row.original

        return (
          <QuotesTableStaticActions
            convertToFinalInvoiceUrl={convertToFinalInvoiceUrl}
            convertToInvoiceUrl={convertToInvoiceUrl}
            documentLocale={documentLocale}
            documentType={documentType}
            duplicateUrl={duplicateUrl}
            editUrl={editUrl}
            estimateNumber={estimateNumber}
            externalContactId={externalContactId}
            quoteId={id}
            quoteStatus={quoteStatus}
          />
        )
      },
      Header: '',
    }

    return [...mappedColumns, staticActionsColumn]
  }, [activeColumns])

  return { columns }
}
